import { useState, useRef, useEffect, useCallback } from "react";
import { PDFDocument, PDFHexString, PDFName, PDFArray, rgb, StandardFonts, degrees } from "pdf-lib";
import { OTHER_FIELD_TOOLTIP, otherFieldName, parseOtherFieldName } from "./signFields";
import { pageView } from "./pageView";
import { fitContain, centredOrigin, signBlocker } from "./signPlace";
import { stampOutputMetadata, stemOfPdfName, meaningfulAuthor } from "./pdfMetadata";
import { ToolContainer, FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Eraser, RotateCcw, Plus, Trash2, MapPin, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { loadPdfDocument } from "./utils";
import { drawTextAnywhere } from "./drawTextAnywhere";
import { ToolInfoSection } from "@/components/ToolInfoSection";

const RENDER_SCALE = 1.5;
const SIGNATURE_FONTS = [
  { family: "Dancing Script", label: "Flowing" },
  { family: "Caveat", label: "Quick pen" },
  { family: "Homemade Apple", label: "Handwritten" },
  { family: "Great Vibes", label: "Formal" },
  { family: "Sacramento", label: "Slim script" },
  { family: "Reenie Beanie", label: "Scribble" },
] as const;
const SIGNATURE_FONT_FILES: Record<string, string> = {
  "Dancing Script": "dancing-script-latin-600", Caveat: "caveat-latin-600", "Homemade Apple": "homemade-apple-latin-400",
  "Great Vibes": "great-vibes-latin-400", Sacramento: "sacramento-latin-400", "Reenie Beanie": "reenie-beanie-latin-400",
};
/** Self-hosted (SIL Open Font License), so signatures look the same everywhere and nothing is requested from a third party. */
const SIGNATURE_FONT_CSS = Object.entries(SIGNATURE_FONT_FILES)
  .map(([family, file]) => `@font-face{font-family:'${family}';src:url(/fonts/signature/${file}-normal.woff2) format('woff2');font-display:swap}`).join("");

const TOOL_INFO_CONTENT = {
  what: `Adds one or more signature, initial, date, or name fields to a PDF — for your own signature, embedded directly into the document, or for someone else's, left as empty form fields for them to fill in, in any PDF reader or in this same tool.`,
  how: `Enter your name and initials, set up how your signature and initials should look (drawn, typed in a signature-style font, or an image you already have), then add fields to the list: pick a type, who it's for, and which page. Click the page preview to place each field, then generate the PDF. If any fields are marked for someone else, send them the downloaded file (and the Sign PDF link if you like) — their fields are found automatically when they open it here.`,
  tip: `Fields you assign to yourself get embedded permanently into the page. Fields marked for the other party are left as empty form fields at that spot. When they open the file in this tool, those fields are found and placed for them automatically, so they get the same signature options (draw, type, or import) you used.`,
};

type MarkMode = "import" | "auto" | "custom";
type DateFormat = "mm/dd/yyyy" | "dd/mm/yyyy";
type FieldType = "signature" | "initial" | "date" | "fullName" | "initialsText";
type AssignedTo = "me" | "other";

interface SignField {
  id: string;
  type: FieldType;
  assignedTo: AssignedTo;
  page: number;
  // "single" (the real default, unchanged from before): applies only
  // to `page`. "all": every page in the document. "range": the pages
  // parsed from pageRange, a real range string like "1-3, 5, 6,
  // 8-11" -- the field's own placed position (xFrac/yFrac) is reused
  // at the same relative spot on every page it applies to, rather
  // than asking for separate placement per page.
  pageMode: "single" | "all" | "range";
  pageRange: string;
  xFrac: number | null;
  yFrac: number | null;
}

/** Parses a real page-range string ("1-3, 5, 6, 8-11") into a sorted,
 *  deduplicated, in-bounds list of page numbers. Silently drops
 *  anything malformed or out of range rather than throwing -- this
 *  runs on every keystroke while someone is still typing the range,
 *  so a half-finished "1-" or a stray comma needs to degrade
 *  gracefully, not error. */
function parsePageRange(rangeStr: string, pageCount: number): number[] {
  const pages = new Set<number>();
  for (const part of rangeStr.split(",")) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const rangeMatch = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
    if (rangeMatch) {
      const start = Math.max(1, parseInt(rangeMatch[1], 10));
      const end = Math.min(pageCount, parseInt(rangeMatch[2], 10));
      for (let p = start; p <= end; p++) pages.add(p);
    } else if (/^\d+$/.test(trimmed)) {
      const p = parseInt(trimmed, 10);
      if (p >= 1 && p <= pageCount) pages.add(p);
    }
  }
  return Array.from(pages).sort((a, b) => a - b);
}

/** Resolves a field down to the real list of pages it actually
 *  applies to -- "single" and empty/invalid "range" input both fall
 *  back to the field's own single `page`, so a field always applies
 *  to at least the page it was placed on. */
function resolveFieldPages(field: SignField, pageCount: number): number[] {
  if (field.pageMode === "all") return Array.from({ length: pageCount }, (_, i) => i + 1);
  if (field.pageMode === "range") {
    const parsed = parsePageRange(field.pageRange, pageCount);
    return parsed.length > 0 ? parsed : [field.page];
  }
  return [field.page];
}

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  signature: "Signature",
  initial: "Initial",
  date: "Date",
  fullName: "Full Name",
  initialsText: "Initials (text)",
};

// Roughly matches each field type's real content so the placement box on
// the preview looks about the size the final content will actually be --
// a signature needs more room than a date, for instance.
const FIELD_SIZE_FRAC: Record<FieldType, { width: number; height: number }> = {
  signature: { width: 0.22, height: 0.06 },
  initial: { width: 0.08, height: 0.05 },
  date: { width: 0.14, height: 0.035 },
  fullName: { width: 0.2, height: 0.035 },
  initialsText: { width: 0.08, height: 0.035 },
};


function todayFormatted(format: DateFormat): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return format === "mm/dd/yyyy" ? `${mm}/${dd}/${yyyy}` : `${dd}/${mm}/${yyyy}`;
}

function makeId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export default function SignPdf() {
  // Touch screens get "Tap", mouse and trackpad get "Click".
  const [coarsePointer] = useState(() => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches);
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(1);
  const [pageNum, setPageNum] = useState(1);

  const [fullName, setFullName] = useState("");
  const [sigFont, setSigFont] = useState<string>(SIGNATURE_FONTS[0].family);
  const [initials, setInitials] = useState("");
  const [dateFormat, setDateFormat] = useState<DateFormat>("mm/dd/yyyy");

  const [signatureMode, setSignatureMode] = useState<MarkMode>("auto");
  const [signatureImage, setSignatureImage] = useState<string | null>(null); // data URL, from import or custom draw
  const [initialsMode, setInitialsMode] = useState<MarkMode>("auto");
  const [initialsImage, setInitialsImage] = useState<string | null>(null);

  const [fields, setFields] = useState<SignField[]>([]);
  const [placingFieldId, setPlacingFieldId] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [notice, setNotice] = useState("");

  const pageCanvasRef = useRef<HTMLCanvasElement>(null);
  const sigDrawCanvasRef = useRef<HTMLCanvasElement>(null);
  const initialsDrawCanvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingSig = useRef(false);
  const isDrawingInitials = useRef(false);
  const sigImportInputRef = useRef<HTMLInputElement>(null);
  const initialsImportInputRef = useRef<HTMLInputElement>(null);

  // Load a real signature-style font just for this page, rather than the
  // whole site -- the auto-generated signature/initials preview and the
  // final embedded image both use it.
  useEffect(() => {
    if (document.getElementById("fdp-signature-fonts")) return;
    const st = document.createElement("style");
    st.id = "fdp-signature-fonts";
    st.textContent = SIGNATURE_FONT_CSS;
    document.head.appendChild(st);
  }, []);

  const waitingNamesRef = useRef<Set<string>>(new Set());

  /** Fields another person left empty for you (fdp_*_other): list them as your own fields, already placed. */
  const loadWaitingFields = async (f: File) => {
    try {
      const pdfDoc = await PDFDocument.load(await f.arrayBuffer(), { ignoreEncryption: true });
      const pages = pdfDoc.getPages();
      const found: SignField[] = [];
      for (const fld of pdfDoc.getForm().getFields()) {
        const info = parseOtherFieldName(fld.getName());
        if (!info) continue;
        const widget = fld.acroField.getWidgets()[0];
        if (!widget) continue;
        const wref = pdfDoc.context.getObjectRef(widget.dict);
        const pageIdx = pages.findIndex((pg) => pg.node.lookupMaybe(PDFName.of("Annots"), PDFArray)?.asArray().some((a) => a === wref));
        if (pageIdx < 0) continue;
        const r = widget.getRectangle();
        const view = pageView(pages[pageIdx]);
        const c = view.toViewed(r.x + r.width / 2, r.y + r.height / 2);
        found.push({ id: makeId(), type: info.type, assignedTo: "me", page: pageIdx + 1, pageMode: "single", pageRange: "", xFrac: c.x / view.width, yFrac: 1 - c.y / view.height });
        waitingNamesRef.current.add(fld.getName());
      }
      if (found.length > 0) {
        setFields(found);
        toast.info(`${found.length} field${found.length === 1 ? " is" : "s are"} waiting for you — they're already placed. Set up your signature, then download.`);
      }
    } catch { /* a PDF without fields is the normal case */ }
  };

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0];
    if (!f) return;
    setFile(f);
    waitingNamesRef.current = new Set();
    try {
      const doc = await loadPdfDocument(f);
      setPageCount(doc.numPages);
      setPageNum(1);
      await loadWaitingFields(f);
    } catch {
      toast.error("Couldn't read that PDF. It may be corrupted or password-protected.");
      setFile(null);
    }
  }, []);

  // Render the selected page whenever the file or page number changes.
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    (async () => {
      const doc = await loadPdfDocument(file);
      const page = await doc.getPage(pageNum);
      const viewport = page.getViewport({ scale: RENDER_SCALE });
      const canvas = pageCanvasRef.current;
      if (!canvas || cancelled) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      await page.render({ canvasContext: ctx, viewport, canvas, intent: "print" }).promise;
    })();
    return () => { cancelled = true; };
  }, [file, pageNum]);

  // --- Auto-generated signature/initials preview, rendered fresh whenever
  // the underlying name/initials or the selected mode changes.
  const renderAutoMark = useCallback((text: string, fontSize: number): string | null => {
    const t = text.trim();
    if (!t) return null;
    // Measure first, then size the picture to the text, so no name is ever cut off.
    const font = (px: number) => `${px}px '${sigFont}', cursive`;
    const probe = document.createElement("canvas").getContext("2d")!;
    probe.font = font(fontSize);
    const MAX_W = 900;
    let size = fontSize;
    let w = probe.measureText(t).width;
    if (w > MAX_W) { size = Math.max(12, Math.floor(fontSize * MAX_W / w)); probe.font = font(size); w = probe.measureText(t).width; }
    const pad = Math.ceil(size * 0.35);
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(w) + pad * 2;
    canvas.height = Math.ceil(size * 1.8) + pad;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#1e293b";
    ctx.font = font(size);
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(t, pad, canvas.height / 2);
    // Trim the empty edges so the stamp on the page is as large as the writing.
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let x0 = canvas.width, x1 = -1, y0 = canvas.height, y1 = -1;
    for (let y = 0; y < canvas.height; y++) for (let x = 0; x < canvas.width; x++) {
      if (data[(y * canvas.width + x) * 4 + 3] > 8) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
    if (x1 < 0) return canvas.toDataURL("image/png");
    const m = 4;
    const cw = x1 - x0 + 1 + m * 2, ch = y1 - y0 + 1 + m * 2;
    const out = document.createElement("canvas");
    out.width = cw; out.height = ch;
    out.getContext("2d")!.drawImage(canvas, x0 - m, y0 - m, cw, ch, 0, 0, cw, ch);
    return out.toDataURL("image/png");
  }, [sigFont]);

  const [autoSignaturePreview, setAutoSignaturePreview] = useState<string | null>(null);
  const [autoInitialsPreview, setAutoInitialsPreview] = useState<string | null>(null);

  useEffect(() => {
    // document.fonts.ready ensures the script font is actually loaded
    // before we render text with it -- otherwise canvas silently falls
    // back to a default font on the very first render.
    let live = true;
    Promise.race([document.fonts.load(`56px '${sigFont}'`, fullName + initials + "Aa"), new Promise((r) => setTimeout(r, 3000))]).catch(() => {}).then(() => {
      if (!live) return;
      setAutoSignaturePreview(renderAutoMark(fullName, 56));
      setAutoInitialsPreview(renderAutoMark(initials, 56));
    });
    return () => { live = false; };
  }, [fullName, initials, renderAutoMark]);

  // --- Custom-drawn signature/initials pads ---
  const getCanvasPoint = (canvasRef: React.RefObject<HTMLCanvasElement | null>, e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const makeDrawHandlers = (
    canvasRef: React.RefObject<HTMLCanvasElement | null>,
    isDrawing: React.MutableRefObject<boolean>,
    onStroke: () => void
  ) => ({
    onPointerDown: (e: React.PointerEvent<HTMLCanvasElement>) => {
      isDrawing.current = true;
      const ctx = canvasRef.current?.getContext("2d");
      const pt = getCanvasPoint(canvasRef, e);
      if (!ctx) return;
      ctx.beginPath();
      ctx.moveTo(pt.x, pt.y);
    },
    onPointerMove: (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawing.current) return;
      const ctx = canvasRef.current?.getContext("2d");
      const pt = getCanvasPoint(canvasRef, e);
      if (!ctx) return;
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#1e293b";
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
      onStroke();
    },
    onPointerUp: () => { isDrawing.current = false; },
    onPointerLeave: () => { isDrawing.current = false; },
  });

  const clearDrawCanvas = (canvasRef: React.RefObject<HTMLCanvasElement | null>, setImage: (v: string | null) => void) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setImage(null);
  };

  const captureDrawnCanvas = (canvasRef: React.RefObject<HTMLCanvasElement | null>, setImage: (v: string) => void) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setImage(canvas.toDataURL("image/png"));
  };

  // Real bug, confirmed directly: handleDownload always calls
  // pdfDoc.embedPng() on whatever gets imported here (see below), but
  // the file picker accepts any image type, and a real, valid JPEG
  // (or WEBP, GIF, etc.) is not valid PNG data -- pdf-lib throws.
  // Confirmed with pdf-lib directly, outside the browser: embedPng on
  // real JPEG bytes fails every time; embedJpg on the same bytes
  // works correctly. Rather than branch on MIME type and maintain two
  // embed paths (and still fail on WEBP/GIF/BMP, which pdf-lib can't
  // embed at all), this draws whatever was imported onto a canvas and
  // re-exports it as real PNG bytes -- the same real technique the
  // "Draw manually" mode already uses for its own captured image, so
  // handleDownload's unconditional embedPng() call is correct for
  // every import from here on, regardless of the source format.
  const normalizeImageToPng = (dataUrl: string): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Couldn't read this image"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => reject(new Error("Couldn't read this image"));
      img.src = dataUrl;
    });

  const handleImport = (setImage: (v: string) => void, setMode: (m: MarkMode) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      normalizeImageToPng(reader.result as string)
        .then((png) => {
          setImage(png);
          // Choosing a file is a real, unambiguous statement of intent
          // -- "auto" mode's own resolveMarkImage completely ignores
          // an imported image while that mode is still selected,
          // which was the real cause of a confusing downstream error
          // ("Set up your signature first") for anyone who imported an
          // image but never separately clicked the Import/Paste radio.
          // Selecting a file now sets the mode itself, rather than
          // requiring a second, easy-to-miss click to make an already-
          // provided image actually count.
          setMode("import");
        })
        .catch(() => toast.error("Couldn't read that image — try a different file."));
    };
    reader.readAsDataURL(f);
  };

  // Resolves whichever mode is selected down to one actual image to embed.
  // "import" and "custom" both write to the same state variable as the
  // user interacts (the file picker and the draw canvas's onStroke both
  // call the same setter) -- so checking that state directly, rather
  // than re-reading the canvas, is what correctly returns null when
  // "custom" is selected but nothing has actually been drawn yet, instead
  // of silently producing a blank-but-valid PNG that would pass
  // validation and embed as an invisible mark.
  const resolveMarkImage = (mode: MarkMode, currentImage: string | null, auto: string | null): string | null => {
    return mode === "auto" ? auto : currentImage;
  };
  // Computed once here, at component level, rather than inline in
  // handleDownload only -- the live page preview needs the exact same
  // resolved image to show a real preview of a placed signature/
  // initial field, not a second, possibly-diverging computation of
  // "what the signature currently is."
  const resolvedSignaturePreview = resolveMarkImage(signatureMode, signatureImage, autoSignaturePreview);
  const resolvedInitialsPreview = resolveMarkImage(initialsMode, initialsImage, autoInitialsPreview);

  // --- Field list management ---
  const addField = (type: FieldType) => {
    const newField: SignField = { id: makeId(), type, assignedTo: "me", page: pageNum, pageMode: "single", pageRange: "", xFrac: null, yFrac: null };
    setFields(f => [...f, newField]);
    setPlacingFieldId(newField.id);
    toast.info("Click the page preview to place this field");
  };

  const updateField = (id: string, patch: Partial<SignField>) => {
    setFields(f => f.map(field => field.id === id ? { ...field, ...patch } : field));
  };

  const removeField = (id: string) => {
    setFields(f => f.filter(field => field.id !== id));
    if (placingFieldId === id) setPlacingFieldId(null);
  };

  const handlePageClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!placingFieldId) return;
    const canvas = pageCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const xFrac = (e.clientX - rect.left) / rect.width;
    const yFrac = (e.clientY - rect.top) / rect.height;
    updateField(placingFieldId, { xFrac, yFrac, page: pageNum });
    setPlacingFieldId(null);
  };

  // Confirmed as a real, reported gap, not assumed: once placed, a
  // field had no way to be moved at all -- its overlay div was
  // pointer-events-none, so it couldn't even receive a pointerdown in
  // the first place. Same coordinate math as handlePageClick above
  // (the canvas's own bounding rect, fraction of width/height), driven
  // by pointer capture instead of a single click so the drag keeps
  // tracking the field even if the pointer briefly leaves its small
  // box while moving fast. draggingFieldId is a ref, not state --
  // every pointermove already drives a real state update through
  // updateField, so a second render-triggering state wrapped around
  // the same gesture would be redundant.
  const draggingFieldId = useRef<string | null>(null);

  const startFieldDrag = (e: React.PointerEvent<HTMLDivElement>, fieldId: string) => {
    e.stopPropagation();
    draggingFieldId.current = fieldId;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const dragField = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingFieldId.current) return;
    const canvas = pageCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    // Clamped to [0, 1] so a fast drag can't place a field's center
    // outside the page entirely, where it would become unreachable
    // (no way to select or drag something you can no longer see).
    const xFrac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const yFrac = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    updateField(draggingFieldId.current, { xFrac, yFrac });
  };

  const endFieldDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingFieldId.current) return;
    draggingFieldId.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // --- Generate the final PDF ---
  const handleDownload = async () => {
    if (!file) return;
    const blocker = signBlocker(fields);
    if (blocker) {
      setNotice(blocker);
      toast.error(blocker);
      return;
    }
    setNotice("");

    const meFieldsNeedingSignature = fields.some(f => f.assignedTo === "me" && f.type === "signature");
    const meFieldsNeedingInitials = fields.some(f => f.assignedTo === "me" && f.type === "initial");
    const resolvedSignature = resolvedSignaturePreview;
    const resolvedInitials = resolvedInitialsPreview;

    if (meFieldsNeedingSignature && !resolvedSignature) {
      setNotice("Set up your signature first (above the field list).");
      toast.error("Set up your signature first (above the field list)");
      return;
    }
    if (meFieldsNeedingInitials && !resolvedInitials) {
      toast.error("Set up your initials first (above the field list)");
      return;
    }

    try {
      const pdfBytes = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes, { updateMetadata: false });
      // Keep what the source says about itself (title, author, subject); placeholders like "untitled" are dropped.
      const srcTitle = pdfDoc.getTitle(), srcAuthor = pdfDoc.getAuthor(), srcSubject = pdfDoc.getSubject();
      const pages = pdfDoc.getPages();
      const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);

      // Cache embedded images per data URL so the same signature/initials
      // image isn't re-embedded once per field if used on multiple fields.
      const embeddedImageCache = new Map<string, Awaited<ReturnType<typeof pdfDoc.embedPng>>>();
      const getEmbeddedImage = async (dataUrl: string) => {
        if (embeddedImageCache.has(dataUrl)) return embeddedImageCache.get(dataUrl)!;
        const bytes = await (await fetch(dataUrl)).arrayBuffer();
        const img = await pdfDoc.embedPng(bytes);
        embeddedImageCache.set(dataUrl, img);
        return img;
      };

      let otherPartyFieldCount = 0;

      for (const field of fields) {
        const size = FIELD_SIZE_FRAC[field.type];
        for (const pageIdx of resolveFieldPages(field, pages.length)) {
          const page = pages[pageIdx - 1];
          // Placed as the reader sees the page (turned, cropped); `put` maps that to the stored page (pageView.ts).
          const view = pageView(page);
          const { width: pageWidthPt, height: pageHeightPt } = view;
          const rotDeg = view.rotation;
          const rot = degrees(rotDeg);
          const put = (vx: number, vy: number) => view.toStored(vx, vy);
          const widthPt = pageWidthPt * size.width;
          const heightPt = pageHeightPt * size.height;
          // Canvas/click coords have origin top-left; PDF points have origin
          // bottom-left, so the Y axis has to be flipped here.
          const cx = field.xFrac! * pageWidthPt;
          const cy = pageHeightPt - field.yFrac! * pageHeightPt;
          const { x: xPt, y: yPt } = centredOrigin(cx, cy, widthPt, heightPt);
          // Images are drawn to fit the box and centred on the click, as the preview shows them.
          const drawMark = (img: { width: number; height: number }) => {
            const fit = fitContain(widthPt, heightPt, img.height / img.width);
            const o = centredOrigin(cx, cy, fit.width, fit.height);
            const p0 = put(o.x, o.y);
            return { p0, fit };
          };

          if (field.assignedTo === "other") {
            // A real, empty form field at this spot: Adobe, Preview and phone readers show a clickable box, and
            // Sign PDF finds it again when the file is reopened here (see parseOtherFieldName).
            otherPartyFieldCount += 1;
            const pa = put(xPt, yPt), pb = put(xPt + widthPt, yPt + heightPt);
            const tf = pdfDoc.getForm().createTextField(otherFieldName(field.type, otherPartyFieldCount));
            tf.acroField.dict.set(PDFName.of("TU"), PDFHexString.fromText(OTHER_FIELD_TOOLTIP[field.type]));
            tf.addToPage(page, {
              x: Math.min(pa.x, pb.x), y: Math.min(pa.y, pb.y), width: Math.abs(pb.x - pa.x), height: Math.abs(pb.y - pa.y),
              borderColor: rgb(0.11, 0.6, 0.55), borderWidth: 1, backgroundColor: rgb(0.93, 0.98, 0.97),
            });
            continue;
          }

          // assignedTo === "me": embed real, final content directly.
          if (field.type === "signature" && resolvedSignature) {
            const img = await getEmbeddedImage(resolvedSignature);
          const { p0, fit } = drawMark(img);
          page.drawImage(img, { x: p0.x, y: p0.y, width: fit.width, height: fit.height, rotate: rot });
        } else if (field.type === "initial" && resolvedInitials) {
          const img = await getEmbeddedImage(resolvedInitials);
          const { p0, fit } = drawMark(img);
          page.drawImage(img, { x: p0.x, y: p0.y, width: fit.width, height: fit.height, rotate: rot });
        } else if (field.type === "date") {
          { const txt = todayFormatted(dateFormat); const o = centredOrigin(cx, cy, helvetica.widthOfTextAtSize(txt, 11), 8); const p0 = put(o.x, o.y); page.drawText(txt, { x: p0.x, y: p0.y, size: 11, font: helvetica, color: rgb(0.1, 0.1, 0.1), rotate: rot }); }
        } else if (field.type === "fullName") {
          await drawTextAnywhere(pdfDoc, page, fullName.trim() || "(name not entered)", { ...put(xPt, yPt), size: 11, color: { r: 0.1, g: 0.1, b: 0.1 }, rotateDegrees: rotDeg });
        } else if (field.type === "initialsText") {
          await drawTextAnywhere(pdfDoc, page, initials.trim() || "(initials not entered)", { ...put(xPt, yPt), size: 11, color: { r: 0.1, g: 0.1, b: 0.1 }, rotateDegrees: rotDeg });
        }
        }
      }

      // Fields the other party left for you (found when this file was opened) have been filled in above: remove the empty ones.
      for (const f of pdfDoc.getForm().getFields()) if (parseOtherFieldName(f.getName()) && waitingNamesRef.current.has(f.getName())) pdfDoc.getForm().removeField(f);
      stampOutputMetadata(pdfDoc, { title: srcTitle, author: srcAuthor, fallbackTitle: `Signed ${stemOfPdfName(file.name)}` });
      const subject = meaningfulAuthor(srcSubject) && !/^unspecified$/i.test(srcSubject ?? "") ? srcSubject : undefined;
      if (subject) pdfDoc.setSubject(subject);

      const signedBytes = await pdfDoc.save();
      downloadBlob(new Blob([new Uint8Array(signedBytes)], { type: "application/pdf" }), `signed-${file.name}`);
      toast.success(otherPartyFieldCount > 0
        ? `Signed and downloaded! ${otherPartyFieldCount} field${otherPartyFieldCount === 1 ? "" : "s"} marked for the other party — send them the file — they can sign it in any PDF reader, or here in Sign PDF.`
        : "Signed PDF downloaded!");
    } catch (err) {
      console.error("Signing failed:", err);
      toast.error("Something went wrong generating the signed PDF — try again");
    }
  };

  const resetAll = () => {
    setFile(null);
    setFields([]);
    setPlacingFieldId(null);
    setSignatureImage(null);
    setInitialsImage(null);
  };

  const markOptionRow = (
    label: string,
    mode: MarkMode,
    setMode: (m: MarkMode) => void,
    autoPreview: string | null,
    drawnRef: React.RefObject<HTMLCanvasElement | null>,
    isDrawing: React.MutableRefObject<boolean>,
    setDrawnImage: (v: string | null) => void,
    importedImage: string | null,
    setImportedImage: (v: string) => void,
    importInputRef: React.RefObject<HTMLInputElement | null>,
    typedValue: string
  ) => (
    <div className="space-y-2 border rounded-lg p-3">
      <Label className="block font-medium">{label}</Label>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="flex items-start gap-2 border rounded-lg p-2 cursor-pointer hover:bg-muted/40">
          <input type="radio" checked={mode === "import"} onChange={() => { setMode("import"); setDrawnImage(null); }} className="mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium mb-1">Import / Paste</div>
            {importedImage ? (
              <div className="bg-white rounded p-1 inline-block">
                <img src={importedImage} alt="Imported" className="h-10 object-contain" />
              </div>
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={() => importInputRef.current?.click()}>Choose image</Button>
            )}
            <input ref={importInputRef} type="file" accept="image/*" className="hidden" onChange={handleImport(setImportedImage, setMode)} />
          </div>
        </label>

        <label className="flex items-start gap-2 border rounded-lg p-2 cursor-pointer hover:bg-muted/40">
          <input type="radio" checked={mode === "auto"} onChange={() => setMode("auto")} className="mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium mb-1">Auto-generated</div>
            {autoPreview ? (
              <div className="bg-white rounded p-1 inline-block">
                <img src={autoPreview} alt="Auto-generated" className="h-10 object-contain" />
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">{typedValue ? "..." : "Type a value above first"}</span>
            )}
          </div>
        </label>

        <label className="flex items-start gap-2 border rounded-lg p-2 cursor-pointer hover:bg-muted/40">
          <input type="radio" checked={mode === "custom"} onChange={() => { setMode("custom"); setDrawnImage(null); }} className="mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium mb-1">Draw manually</div>
            <div className="flex items-center gap-2">
              <canvas
                ref={drawnRef}
                width={300}
                height={90}
                {...makeDrawHandlers(drawnRef, isDrawing, () => captureDrawnCanvas(drawnRef, (v) => setDrawnImage(v)))}
                className="border border-dashed rounded bg-white touch-none cursor-crosshair w-full max-w-[180px] h-[54px]"
              />
              <Button type="button" variant="outline" size="icon" className="shrink-0 h-7 w-7" onClick={() => clearDrawCanvas(drawnRef, setDrawnImage)} title="Clear">
                <Eraser className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </label>
      </div>
    </div>
  );

  return (
    <ToolContainer>
      {/* Above-fold trust strip -- states both halves plainly before
          anyone starts using the tool, rather than only in the
          editorial content further down the page: this genuinely runs
          entirely in your browser (a real, positive claim worth
          surfacing early), and it's a stamped/embedded image of a
          signature, not a certified digital signature with
          cryptographic identity verification (an honest limit worth
          surfacing just as early, not just in a "when not to use"
          section someone may not scroll to). */}
      <div className="mb-4 p-3 rounded-lg border bg-muted/50 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
        <span>🔒 Runs entirely in your browser — never uploaded</span>
        <span>✍️ A stamped signature image, not a certified digital signature</span>
      </div>

      {/* Everything below is shown from the start, not gated behind a
          file upload — someone deciding whether this tool has what
          they need (signature options, other-party fields, date
          formats) shouldn't have to upload a real document first just
          to find out. Only the parts that genuinely need a real PDF --
          the page preview/field placement, and the final download --
          stay gated on `file` below. */}
      <div className="space-y-6">
        {!file && (
          <div data-testid="sign-step-upload" className="space-y-2">
            <p className="font-medium text-sm">Step 1: Add the PDF you want to sign</p>
            <FileDropZone gateAtDownload
              accept="application/pdf"
              onFiles={handleFiles}
              label="Drop a PDF to sign"
              description="Add your signature, initials, dates, or fields for someone else to fill in"
            />
            <p className="font-medium text-sm pt-2">Step 2: Create your signature</p>
          </div>
        )}
        <div className="space-y-3 max-w-sm">
          <div>
            <Label htmlFor="sign-full-name" className="mb-1 block">Full Name</Label>
            <Input id="sign-full-name" autoComplete="name" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Jane Q. Public" />
          </div>
          <div>
            <Label htmlFor="sign-initials" className="mb-1 block">Initials</Label>
            <Input id="sign-initials" value={initials} onChange={e => setInitials(e.target.value)} placeholder="JQP" />
          </div>
          <div>
            <Label className="mb-1 block">Signature style</Label>
            <div className="flex flex-wrap gap-2">
              {SIGNATURE_FONTS.map((f) => (
                <button key={f.family} type="button" onClick={() => setSigFont(f.family)} aria-pressed={sigFont === f.family}
                  className={`border rounded-md px-3 py-1 text-lg leading-tight ${sigFont === f.family ? "border-primary bg-primary/10" : "hover:bg-muted/40"}`}
                  style={{ fontFamily: `'${f.family}', cursive` }} title={f.label}>
                  {(fullName.trim() || "Signature").slice(0, 14)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {markOptionRow(
          "Your Signature", signatureMode, setSignatureMode, autoSignaturePreview,
          sigDrawCanvasRef, isDrawingSig, setSignatureImage,
          signatureImage, setSignatureImage, sigImportInputRef, fullName
        )}

        {markOptionRow(
          "Your Initials", initialsMode, setInitialsMode, autoInitialsPreview,
          initialsDrawCanvasRef, isDrawingInitials, setInitialsImage,
          initialsImage, setInitialsImage, initialsImportInputRef, initials
        )}

        <div className="border rounded-lg p-3">
          <Label className="block font-medium mb-2">Date Format</Label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={dateFormat === "mm/dd/yyyy"} onChange={() => setDateFormat("mm/dd/yyyy")} />
              <span className="text-sm">MM/DD/YYYY <span className="text-muted-foreground">(US)</span></span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={dateFormat === "dd/mm/yyyy"} onChange={() => setDateFormat("dd/mm/yyyy")} />
              <span className="text-sm">DD/MM/YYYY <span className="text-muted-foreground">(most other countries)</span></span>
            </label>
          </div>
        </div>

        {file && pageCount > 1 && (
          <div className="flex items-center gap-3">
            <Label htmlFor="sign-page-num" className="shrink-0">Page</Label>
            <Input
              id="sign-page-num"
              type="number" min={1} max={pageCount} value={pageNum}
              onChange={e => setPageNum(Math.min(pageCount, Math.max(1, Number(e.target.value) || 1)))}
              className="w-24"
            />
            <span className="text-sm text-muted-foreground">of {pageCount}</span>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="font-medium">Fields</Label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(FIELD_TYPE_LABELS) as FieldType[]).map(type => (
                <Button
                  key={type} type="button" variant="outline" size="sm" className="gap-1"
                  onClick={() => {
                    if (!file) {
                      toast.info("Drop a PDF below first, then add fields to place them on it");
                      return;
                    }
                    addField(type);
                  }}
                >
                  <Plus className="h-3 w-3" /> {FIELD_TYPE_LABELS[type]}
                </Button>
              ))}
            </div>
          </div>

          {fields.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2 font-medium">#</th>
                    <th className="text-left p-2 font-medium">Type</th>
                    <th className="text-left p-2 font-medium">Assigned to</th>
                    <th className="text-left p-2 font-medium">Page</th>
                    <th className="text-left p-2 font-medium">Placed</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, i) => (
                    <tr key={field.id} className={`border-t ${placingFieldId === field.id ? "bg-primary/5" : ""}`}>
                      <td className="p-2">{i + 1}</td>
                      <td className="p-2">{FIELD_TYPE_LABELS[field.type]}</td>
                      <td className="p-2">
                        <Select value={field.assignedTo} onValueChange={(v) => updateField(field.id, { assignedTo: v as AssignedTo })}>
                          <SelectTrigger aria-label={`Field ${i + 1} assigned to`} className="h-8 w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="me">Me</SelectItem>
                            <SelectItem value="other">Other party</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <div className="flex flex-col gap-1 min-w-[140px]">
                          <div className="flex items-center gap-1">
                            <Input
                              type="number" min={1} max={pageCount} value={field.page}
                              onChange={e => {
                                const newPage = Math.min(pageCount, Math.max(1, Number(e.target.value) || 1));
                                // Changing the page after a field is already placed would
                                // otherwise silently keep its old x/y fraction on the new
                                // page -- reset placement instead so it's obvious the field
                                // needs to be clicked into position again.
                                updateField(field.id, { page: newPage, xFrac: null, yFrac: null });
                              }}
                              className="h-8 w-16"
                              aria-label={`Field ${i + 1} reference page`}
                              title="Reference page — where you click to place this field"
                            />
                            <Select value={field.pageMode} onValueChange={(v) => updateField(field.id, { pageMode: v as SignField["pageMode"] })}>
                              <SelectTrigger aria-label={`Field ${i + 1} pages`} className="h-8 w-24"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="single">This page</SelectItem>
                                <SelectItem value="all">All pages</SelectItem>
                                <SelectItem value="range">Range...</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {field.pageMode === "range" && (
                            <Input
                              value={field.pageRange}
                              onChange={e => updateField(field.id, { pageRange: e.target.value })}
                              aria-label={`Field ${i + 1} page range`}
                              placeholder="e.g. 1-3, 5, 6, 8-11"
                              className="h-8 text-xs"
                            />
                          )}
                          {(field.pageMode === "all" || field.pageMode === "range") && (
                            <p className="text-[10px] text-muted-foreground leading-tight">
                              Placed once, on the page above — the same spot repeats on
                              {field.pageMode === "all" ? ` all ${pageCount} pages` : ` page${parsePageRange(field.pageRange, pageCount).length === 1 ? "" : "s"} ${field.pageRange || "(enter a range)"}`}.
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-2">
                        {field.xFrac !== null ? (
                          <span className="text-green-600 text-xs">✓ placed</span>
                        ) : (
                          <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => { setPageNum(field.page); setPlacingFieldId(field.id); }}>
                            <MapPin className="h-3 w-3" /> Click page to place
                          </Button>
                        )}
                      </td>
                      <td className="p-2">
                        <Button type="button" size="icon" variant="ghost" className="h-7 w-7" aria-label={`Remove field ${i + 1}`} onClick={() => removeField(field.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="border border-dashed rounded-lg p-4 text-sm text-muted-foreground text-center">
              {file ? `${coarsePointer ? "Tap" : "Click"} where you want to sign: add a Signature field above, then ${coarsePointer ? "tap" : "click"} the page.` : "No fields yet. Add your PDF first, then add a Signature field and click the page to place it."}
            </p>
          )}
          {placingFieldId && (
            <p className="text-sm text-primary mt-2">Click the page preview below to place this field.</p>
          )}
        </div>

        <div>
          <Label className="mb-2 block">Page preview</Label>
          {file ? (
            <div className="relative inline-block border rounded-lg overflow-hidden bg-muted/30 max-w-full">
              <canvas
                ref={pageCanvasRef}
                onClick={handlePageClick}
                className={`max-w-full h-auto block ${placingFieldId ? "cursor-crosshair" : ""}`}
              />
              {fields.filter(f => f.xFrac !== null && resolveFieldPages(f, pageCount).includes(pageNum)).map(field => {
                const size = FIELD_SIZE_FRAC[field.type];
                // A real preview of what this field will actually
                // contain once generated -- not just its type name.
                // "other party" fields still show the plain label,
                // correctly: there's nothing of theirs to preview yet,
                // and the real PDF itself only draws a marker + label
                // for those, not real content, so this matches what
                // will actually end up on the page either way.
                let previewContent: React.ReactNode = FIELD_TYPE_LABELS[field.type];
                if (field.assignedTo === "me") {
                  if (field.type === "signature" && resolvedSignaturePreview) {
                    previewContent = <img src={resolvedSignaturePreview} alt="" className="max-w-full max-h-full object-contain" />;
                  } else if (field.type === "initial" && resolvedInitialsPreview) {
                    previewContent = <img src={resolvedInitialsPreview} alt="" className="max-w-full max-h-full object-contain" />;
                  } else if (field.type === "date") {
                    previewContent = todayFormatted(dateFormat);
                  } else if (field.type === "fullName") {
                    previewContent = fullName || FIELD_TYPE_LABELS[field.type];
                  } else if (field.type === "initialsText") {
                    previewContent = initials || FIELD_TYPE_LABELS[field.type];
                  }
                }
                return (
                  <div
                    key={field.id}
                    onPointerDown={(e) => startFieldDrag(e, field.id)}
                    onPointerMove={dragField}
                    onPointerUp={endFieldDrag}
                    className={`absolute border-2 rounded flex items-center justify-center text-[9px] font-medium overflow-hidden cursor-move touch-none ${field.assignedTo === "me" ? "border-primary bg-primary/10" : "border-blue-500 bg-blue-500/10"}`}
                    style={{
                      left: `${field.xFrac! * 100}%`,
                      top: `${field.yFrac! * 100}%`,
                      width: `${size.width * 100}%`,
                      aspectRatio: `${size.width} / ${size.height}`,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    {previewContent}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground border border-dashed rounded-lg p-4 text-center">
              Add your PDF in step 1 above. It appears here so you can place your signature.
            </p>
          )}
        </div>

        {file && fields.some(f => f.assignedTo === "other") && (
          <div className="border border-teal-500/40 bg-teal-500/5 rounded-lg p-3 text-sm space-y-2">
            <p className="font-medium">Before you send this</p>
            <p className="text-muted-foreground">
              Fields marked for the other party become real, empty form fields in the PDF. They can fill them in
              any PDF reader, or open the file in Sign PDF (link below): the fields are found automatically,
              already placed, and they sign with the draw / type / import tools, then download their own copy.
            </p>
            <div className="flex items-center gap-2 pt-1">
              <a
                href="https://www.freedailypro.com/tool/sign-pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-teal-400 hover:text-teal-300 underline underline-offset-2 break-all"
              >
                freedailypro.com/tool/sign-pdf
              </a>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="shrink-0 gap-1.5"
                onClick={() => {
                  navigator.clipboard.writeText("https://www.freedailypro.com/tool/sign-pdf");
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                }}
              >
                {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {linkCopied ? "Copied" : "Copy Link"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Put this link in your message or e-mail — nothing about it is printed on the document itself.
            </p>
          </div>
        )}

        {file && (
          <>
            {notice && <p role="alert" className="text-sm text-destructive">{notice}</p>}
            <div className="flex gap-3">
              <Button onClick={handleDownload} className="gap-2">
                <Download className="h-4 w-4" /> Download Signed PDF
              </Button>
              <Button variant="outline" onClick={resetAll} className="gap-2">
                <RotateCcw className="h-4 w-4" /> Start Over
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Processed entirely in your browser — your file is never uploaded anywhere. Fields assigned to
              yourself are embedded permanently into the page. Fields marked for the other party are
              left as real, empty form fields (nothing is printed on the page). Signatures here are stamped images,
              not certified digital signatures.
            </p>
          </>
        )}
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
